import React from "react";
import { TIME_MACHINE_FLOOR_DATE } from "../utility/keys";

/**
 * Time Machine #114 Phase 4.5 — date-only picker styled to match the existing
 * <WeekPicker>. No typed input. Three controls:
 *   - "‹"  go to previous day (disabled at the historical floor)
 *   - center: shows the picked date in en-US format
 *   - "›"  go to next day (disabled at "today" — we don't time-travel into the future)
 *
 * Plus a "Today" button on the right that snaps the picker to today's date.
 *
 * Props:
 *   value          — Date the picker is currently showing (UTC midnight typically)
 *   onChange       — fn(Date) called when user clicks prev/next/today
 *   disabled       — bool, disables all controls
 *
 * Floor logic: TIME_MACHINE_FLOOR_DATE is the earliest CALENDAR DAY for which
 * a full day-bundle is fully reconstructible. The prev-day button is disabled
 * if the proposed prev day would be before that floor. (Phase 4 used the
 * snapshot moment timestamp; Phase 4.5 needs the day-after-snapshot since
 * day-bundles use 00:00 UTC as baseline reference — see keys.js comment.)
 *
 * Future logic: the next-day button is disabled when value's date is "today" or later.
 */
const DayPicker = ({ value, onChange, disabled }) => {
  const valueDate = value instanceof Date ? value : null;

  // Phase 6 follow-up (2026-05-09): ALL dates emitted by DayPicker are
  // LOCAL-MIDNIGHT Dates — a Date whose .getFullYear/Month/Date() returns
  // the user's calendar day at 00:00 local time. AppContext.enterTimeTravel
  // (after the slider fix on 2026-05-08) extracts LOCAL components from its
  // Date arg, so emitting LOCAL-midnight here makes the round-trip stable.
  //
  // Earlier code emitted UTC-midnight Dates (encoded LOCAL components into
  // Date.UTC(...)). After midnight EDT this caused a day-loss bug:
  //   - new Date() at 5/9 00:31 EDT = 5/9 04:31 UTC
  //   - startOfLocalDayAsUtc → 5/9 LOCAL components → Date.UTC(2026,4,9)
  //     = 2026-05-09T00:00:00Z. In EDT this absolute moment IS 5/8 20:00 EDT.
  //   - enterTimeTravel(date) reads getFullYear/Month/Date (LOCAL) → 5/8.
  //   - Time-travel lands on 5/8 instead of 5/9. ← user-reported bug.
  // Switching DayPicker to local-midnight everywhere closes the gap.
  const todayLocalDay = startOfLocalDay(new Date());
  // TIME_MACHINE_FLOOR_DATE is YYYY-MM-DD; build the LOCAL-midnight Date
  // for that calendar day so floor comparisons stay in the same encoding
  // as the rest of DayPicker.
  const floorDay = parseFloorDateAsLocalDay(TIME_MACHINE_FLOOR_DATE);

  const proposedPrev = valueDate ? addDaysLocal(valueDate, -1) : null;
  const proposedNext = valueDate ? addDaysLocal(valueDate, 1) : null;

  const disableBack =
    disabled ||
    !proposedPrev ||
    !floorDay ||
    proposedPrev.getTime() < floorDay.getTime();
  const disableForward =
    disabled ||
    !proposedNext ||
    proposedNext.getTime() > todayLocalDay.getTime();

  const goPrev = () => {
    if (disableBack || !proposedPrev || typeof onChange !== "function") return;
    onChange(proposedPrev);
  };
  const goNext = () => {
    if (disableForward || !proposedNext || typeof onChange !== "function") return;
    onChange(proposedNext);
  };
  const goToday = () => {
    if (disabled || typeof onChange !== "function") return;
    onChange(todayLocalDay);
  };

  const display = valueDate ? formatDisplay(valueDate) : "—";

  return (
    <div data-testid="day-picker" data-tutorial-target="day-picker" className="flex items-center gap-2">
      <button
        className={`hover:opacity-75 ${disableBack ? "opacity-30 cursor-not-allowed" : ""}`}
        onClick={goPrev}
        disabled={disableBack}
        title={disableBack ? "Earliest historical day reached" : "Previous day"}
        aria-label={disableBack ? "Previous day — historical floor reached" : "Previous day"}
        type="button"
      >
        <span className="arrow arrow-right"></span>
      </button>
      <div
        className="h-full min-w-[140px] flex items-center justify-center bg-yellow-30 text-black px-2 py-1 font-bold"
        data-testid="day-picker-value"
      >
        <span>{display}</span>
      </div>
      <button
        className={`hover:opacity-75 ${disableForward ? "opacity-30 cursor-not-allowed" : ""}`}
        onClick={goNext}
        disabled={disableForward}
        title={disableForward ? "Cannot time-travel into the future" : "Next day"}
        aria-label={disableForward ? "Next day — would be in the future" : "Next day"}
        type="button"
      >
        <span className="arrow"></span>
      </button>
      <button
        className={`px-2 py-1 text-xs font-bold rounded border-2 ${disabled ? "opacity-30 cursor-not-allowed" : "hover:opacity-75"}`}
        style={{ borderColor: "#f5deb3", color: "#f5deb3" }}
        onClick={goToday}
        disabled={disabled}
        title="Jump to today"
        type="button"
      >
        Today
      </button>
    </div>
  );
};

/**
 * Return a Date representing 00:00:00 UTC of the same calendar day as input.
 * Two Date inputs in different timezones but the same UTC date produce the
 * same output — used as a stable key.
 */
function startOfUtcDay(d) {
  if (!(d instanceof Date) || isNaN(d.getTime())) return null;
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
}

/**
 * Return a Date representing 00:00:00 LOCAL of the input's LOCAL calendar
 * day. The resulting Date's .getFullYear/Month/Date() returns the same day
 * the user perceives in their wall clock. This is the canonical encoding
 * AppContext.enterTimeTravel + TimeSlider use post-Phase 6 fix.
 */
function startOfLocalDay(d) {
  if (!(d instanceof Date) || isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
}

/**
 * Add n calendar days (in LOCAL time) to a Date. Use the JS Date
 * constructor with day arithmetic so DST transitions don't drift the
 * result by an hour — the constructor handles month/year rollover and
 * DST adjustments correctly when given out-of-range day values.
 */
function addDaysLocal(d, n) {
  const start = startOfLocalDay(d);
  if (!start) return null;
  return new Date(
    start.getFullYear(),
    start.getMonth(),
    start.getDate() + n,
    0,
    0,
    0,
    0
  );
}

/**
 * Parse a YYYY-MM-DD floor date string and return the LOCAL-midnight Date
 * for that calendar day. Used for the prev-day disable check so the
 * comparison stays in the same encoding as proposedPrev.
 */
function parseFloorDateAsLocalDay(s) {
  if (typeof s !== "string") return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return null;
  const yyyy = parseInt(m[1], 10);
  const mm = parseInt(m[2], 10) - 1;
  const dd = parseInt(m[3], 10);
  if (isNaN(yyyy) || isNaN(mm) || isNaN(dd)) return null;
  return new Date(yyyy, mm, dd, 0, 0, 0, 0);
}

// Retained but unused: pre-Phase-6 helper that encoded LOCAL components
// into UTC midnight. Kept as a deprecated reference in case other callers
// still import it; new callers should use startOfLocalDay above.
// eslint-disable-next-line no-unused-vars
function startOfLocalDayAsUtc(d) {
  if (!(d instanceof Date) || isNaN(d.getTime())) return null;
  return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
}

// eslint-disable-next-line no-unused-vars
function addDaysUtc(d, n) {
  const start = startOfUtcDay(d);
  if (!start) return null;
  return new Date(start.getTime() + n * 24 * 60 * 60 * 1000);
}

/**
 * Format the picker's date display in the user's LOCAL timezone. Post-Phase
 * 6 fix, the input Date is local-midnight, so default (local) formatting
 * renders the correct calendar day. Pre-fix: input was UTC-midnight and the
 * formatter explicitly used `timeZone: "UTC"` to compensate — that
 * mismatched the new local-midnight encoding and would render the WRONG
 * day after midnight EDT.
 */
function formatDisplay(d) {
  try {
    return new Intl.DateTimeFormat(undefined, {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
    }).format(d);
  } catch (e) {
    // Fallback: ISO date portion
    return d.toISOString().slice(0, 10);
  }
}

export { startOfUtcDay, startOfLocalDayAsUtc, addDaysUtc, formatDisplay };
export default DayPicker;
